import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * FilterPill — a single active-filter chip rendered above the Catalog grid.
 * Shows the label type on the left and the selected alias on the right,
 * with a close button that calls `onRemove`. Clicking the body of the pill
 * (anywhere except the × button) surfaces the owning rail section via
 * `onOpen`, which lets the user quickly toggle more values without
 * scrolling the sidebar.
 *
 * The component itself is a button + embedded button; a top-level `<div>`
 * carries no tab stop. We keep the layout flat so both the open-rail
 * action and the remove action are reachable via a single Tab.
 */
export interface FilterPillProps {
  kicker: string;
  value: string;
  onRemove: () => void;
  onOpen?: () => void;
  className?: string;
}

export function FilterPill({ kicker, value, onRemove, onOpen, className }: FilterPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-or-orange-200 bg-or-orange-50 pl-3 pr-1 text-[12px] font-semibold text-or-orange-800',
        className,
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1.5 py-1 text-left focus-visible:outline-none"
        // When no onOpen handler is wired we still keep the inner button so
        // layout stays identical — the button becomes an inert hit target.
        disabled={!onOpen}
      >
        <span className="text-or-orange-500/80">{kicker}:</span>
        <span>{value}</span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter ${value}`}
        className="ml-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full text-or-orange-600 transition hover:bg-or-orange-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </span>
  );
}
